/**
 * ExitIntentModal.jsx
 *
 * Fires when the user's cursor leaves the viewport (desktop) or rapidly
 * scrolls up on a page they've read >60% of (mobile).
 *
 * Score source: /api/audit/start — SAME backend call as the full Audit
 * Report page, so the number here always matches the number there.
 *
 * Flow:
 *  Step 1 — App search (by name) or paste a store URL
 *  Step 2 — Real score card (overall + 4 sub-scores) + email gate
 *  Step 3 — Confirmation + "View Full Report" link
 */

import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, X, Mail, Loader2, Zap, CheckCircle2, Star,
  Search, AlertTriangle, Apple, Smartphone, Lock,
  BarChart2, Image, MessageSquare, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { verdict } from "../../utils/asoScorer";
import AppSearchInput from "./AppSearchInput";
import { BACKEND_URL } from "../../lib/api";

const SESSION_FLAG  = "appfillip_exit_intent_shown";
const MIN_DWELL_MS  = 10000;
const TRIGGER_TOP_PX = 10;
const API = BACKEND_URL;

/* Pages where the modal should never fire */
const SUPPRESS_ON = [
  /^\/free-aso-audit/,
  /^\/audit-report/,
  /^\/build-your-plan/,
  /^\/contact/,
  /^\/audit\//,
  /^\/lp\//,
  /^\/resources\/aso-score-checker/,
];

/* Backend score key → human label + icon */
const SCORE_META = {
  listing:  { label: "Listing Quality",     icon: BarChart2     },
  visuals:  { label: "Visual Assets",       icon: Image         },
  ratings:  { label: "Ratings & Reviews",   icon: MessageSquare },
  freshness:{ label: "Update Freshness",    icon: RefreshCw     },
};

const SAMPLES = [
  { label: "Spotify (iOS)",    url: "https://apps.apple.com/us/app/spotify-music-and-podcasts/id324684580" },
  { label: "Duolingo (Play)",  url: "https://play.google.com/store/apps/details?id=com.duolingo" },
];

export default function ExitIntentModal() {
  const location = useLocation();
  const navigate  = useNavigate();
  const [open, setOpen] = useState(false);

  /* — Step 1: search / paste — */
  const [mode, setMode]           = useState("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [pasteUrl, setPasteUrl]   = useState("");
  const [loading, setLoading]     = useState(false);
  const [scoreError, setScoreError] = useState("");

  /* — Step 2: score result (shape: { audit_id, app, scores, overall_score }) — */
  const [auditData, setAuditData] = useState(null);

  /* — Step 3: email gate — */
  const [email, setEmail]         = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [done, setDone]           = useState(false);

  const armedRef = useRef(false);

  /* ── Trigger logic ─────────────────────────────────────────────── */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("exit") === "1") { setOpen(true); return; }
    if (SUPPRESS_ON.some((rx) => rx.test(location.pathname))) return;
    try { if (sessionStorage.getItem(SESSION_FLAG)) return; } catch {}

    const armTimer = setTimeout(() => { armedRef.current = true; }, MIN_DWELL_MS);

    const fire = () => {
      if (!armedRef.current) return;
      setOpen(true);
      try { sessionStorage.setItem(SESSION_FLAG, "1"); } catch {}
      cleanup();
    };

    const handleMouseLeave = (e) => {
      if (e.clientY <= TRIGGER_TOP_PX && (e.relatedTarget == null || e.toElement == null)) fire();
    };

    let lastScrollY = window.scrollY, lastScrollAt = Date.now(), maxScrollY = window.scrollY;
    const handleScroll = () => {
      const now = Date.now(), y = window.scrollY;
      maxScrollY = Math.max(maxScrollY, y);
      const dy = y - lastScrollY, dt = now - lastScrollAt;
      lastScrollY = y; lastScrollAt = now;
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH > 0 && maxScrollY / docH > 0.6 && dy < -800 && dt < 600) fire();
    };
    const handleVisibility = () => { if (document.visibilityState === "hidden") fire(); };
    const isTouchDevice = window.matchMedia("(hover: none)").matches;

    document.addEventListener("mouseout", handleMouseLeave);
    if (isTouchDevice) {
      window.addEventListener("scroll", handleScroll, { passive: true });
      document.addEventListener("visibilitychange", handleVisibility);
    }

    const cleanup = () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    return cleanup;
  }, [location.pathname, location.search]);

  /* ── Call the SAME backend endpoint as the full Audit Report ───── */
  const runAudit = async (url) => {
    if (!url) return;
    setLoading(true);
    setScoreError("");
    setAuditData(null);
    try {
      const res = await fetch(`${API}/api/audit/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }
      const data = await res.json();
      setAuditData(data); // { audit_id, app, scores, overall_score }
    } catch (err) {
      setScoreError(err.message || "Couldn't fetch that app. Try another URL.");
    } finally {
      setLoading(false);
    }
  };

  /* Called when the user picks from the AppSearchInput dropdown */
  const pickApp = (app) => runAudit(app.url);

  /* Called for paste-URL mode */
  const handlePasteSubmit = (e) => {
    e?.preventDefault();
    if (!pasteUrl.trim()) { setScoreError("Paste a store URL to begin."); return; }
    runAudit(pasteUrl.trim());
  };

  /* ── Email gate submission ──────────────────────────────────────── */
  const submitEmail = async (e) => {
    e?.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("That email doesn't look right."); return; }
    setEmailLoading(true);
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:          auditData?.app?.name?.split(/[—–-]/)[0]?.trim() || email.split("@")[0],
          email:         email.trim(),
          app_name:      auditData?.app?.name      || undefined,
          app_store_url: auditData?.app?.url        || undefined,
          platform:      auditData?.app?.platform   || undefined,
          audit_score:   auditData?.overall_score   ?? undefined,
          aso_breakdown: auditData?.scores
            ? Object.fromEntries(
                Object.entries(auditData.scores).map(([k, v]) => [
                  k, { score: v, why: SCORE_META[k]?.label || k },
                ])
              )
            : undefined,
          source:  "exit-intent-aso-score",
          message: `Exit-intent ASO score: ${auditData?.overall_score}/100 for ${auditData?.app?.name}. Wants the deep 12-point audit.`,
        }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setDone(true);
      toast.success("Full audit sent — check your inbox 📩");
    } catch (err) {
      toast.error(err?.message || "Could not submit. Try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  const close = () => setOpen(false);

  const v = auditData ? verdict(auditData.overall_score) : null;

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-[90] grid place-items-center px-4 py-6 overflow-y-auto"
          role="dialog" aria-modal="true" aria-labelledby="exit-intent-title"
        >
          {/* Backdrop */}
          <motion.button
            type="button" aria-label="Close" onClick={close}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-charcoal/75 backdrop-blur-sm cursor-pointer"
          />

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            className="relative w-full max-w-lg bg-white border-2 border-charcoal shadow-[12px_12px_0_0_#2196F3] my-auto"
            data-testid="exit-intent-modal"
          >
            <button
              type="button" onClick={close} aria-label="Close"
              className="absolute top-4 right-4 text-zinc-400 hover:text-charcoal z-10"
              data-testid="exit-intent-dismiss"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="h-1.5 bg-electric" aria-hidden />

            <div className="p-5 sm:p-7 md:p-9">

              {/* ── Step 1: Search or paste URL ─────────────────── */}
              {!auditData && !done && (
                <>
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1 mb-4 text-[10px] font-black uppercase tracking-widest">
                    <Zap className="w-3 h-3" /> Wait — one last thing
                  </div>
                  <h3 id="exit-intent-title" className="font-display text-2xl md:text-3xl font-extrabold text-charcoal leading-tight text-balance">
                    Get your app's{" "}
                    <span className="relative inline-block">
                      <span className="relative z-10">real ASO score</span>
                      <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
                    </span>{" "}
                    in 15 seconds.
                  </h3>
                  <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                    The same live score used in our full audit reports — pulled directly from the App Store and Google Play. Free, instant, no email needed.
                  </p>

                  {/* Mode toggle */}
                  <div className="inline-flex border border-zinc-200 mt-5 mb-3 text-[11px] font-bold uppercase tracking-widest">
                    <button
                      type="button"
                      onClick={() => { setMode("search"); setScoreError(""); }}
                      className={`px-3 py-1.5 transition-colors ${mode === "search" ? "bg-charcoal text-white" : "bg-white text-zinc-500 hover:text-charcoal"}`}
                      data-testid="exit-mode-search"
                    >
                      <Search className="inline w-3 h-3 mr-1 -mt-0.5" /> Search by name
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMode("paste"); setScoreError(""); }}
                      className={`px-3 py-1.5 transition-colors ${mode === "paste" ? "bg-charcoal text-white" : "bg-white text-zinc-500 hover:text-charcoal"}`}
                      data-testid="exit-mode-paste"
                    >
                      Paste URL
                    </button>
                  </div>

                  {/* Search mode */}
                  {mode === "search" && (
                    <div data-testid="exit-search-wrapper">
                      <AppSearchInput
                        value={searchQuery}
                        onChange={setSearchQuery}
                        onSelectApp={pickApp}
                      />
                      {loading && (
                        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-electric font-bold">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching live data…
                        </p>
                      )}
                    </div>
                  )}

                  {/* Paste URL mode */}
                  {mode === "paste" && (
                    <form onSubmit={handlePasteSubmit}>
                      <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                        <input
                          type="text"
                          value={pasteUrl}
                          onChange={(e) => setPasteUrl(e.target.value)}
                          placeholder="https://apps.apple.com/… or play.google.com/…"
                          className="w-full border border-zinc-300 px-4 py-3.5 text-sm focus:border-electric outline-none pl-10"
                          autoFocus
                          data-testid="exit-intent-url"
                        />
                      </div>
                      <button
                        type="submit" disabled={loading}
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-electric text-white px-6 py-3.5 font-bold text-sm hover:bg-blue-500 transition-colors disabled:opacity-60"
                        data-testid="exit-intent-score-submit"
                      >
                        {loading
                          ? <><Loader2 className="w-4 h-4 animate-spin" /> Fetching live data…</>
                          : <>Get my ASO score <ArrowRight className="w-4 h-4" /></>
                        }
                      </button>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                        <span className="text-zinc-500 font-bold uppercase tracking-widest">Try:</span>
                        {SAMPLES.map((s) => (
                          <button key={s.label} type="button" onClick={() => setPasteUrl(s.url)}
                            className="text-electric hover:underline">{s.label}</button>
                        ))}
                      </div>
                    </form>
                  )}

                  {scoreError && (
                    <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-600 font-bold">
                      <AlertTriangle className="w-3.5 h-3.5" /> {scoreError}
                    </p>
                  )}

                  <div className="mt-5 pt-4 border-t border-zinc-100 text-center">
                    <div className="inline-flex items-center justify-center gap-1 mb-1.5" aria-hidden>
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-electric text-electric" />)}
                    </div>
                    <p className="text-[11px] text-zinc-500">Trusted by startups backed by Sequoia · a16z · Accel · YC · Lightspeed</p>
                  </div>
                </>
              )}

              {/* ── Step 2: Real score card + email gate ────────── */}
              {auditData && !done && (
                <>
                  {/* App identity */}
                  <div className="flex items-center gap-3 mb-5">
                    {auditData.app?.icon
                      ? <img src={auditData.app.icon} alt="" className="w-12 h-12 rounded-xl border border-zinc-200 flex-shrink-0" />
                      : <span className="w-12 h-12 grid place-items-center bg-electric text-white font-display font-black text-xl flex-shrink-0">{(auditData.app?.name || "?").slice(0, 1)}</span>
                    }
                    <div className="min-w-0">
                      <h3 className="font-display text-lg font-extrabold text-charcoal leading-tight truncate">{auditData.app?.name}</h3>
                      <p className="text-[11px] text-zinc-500 mt-0.5 inline-flex items-center gap-1">
                        {auditData.app?.platform === "ios" ? <Apple className="w-3 h-3" /> : <Smartphone className="w-3 h-3" />}
                        {auditData.app?.developer} · {auditData.app?.platform === "ios" ? "iOS" : "Android"}
                      </p>
                    </div>
                  </div>

                  {/* Overall score ring */}
                  <div className="flex items-center gap-5 mb-5 p-4 bg-zinc-50 border border-zinc-200">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="42" stroke="#e4e4e7" strokeWidth="8" fill="none" />
                        <motion.circle
                          cx="50" cy="50" r="42"
                          stroke={v.color} strokeWidth="8" fill="none" strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 42}
                          initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                          animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - auditData.overall_score / 100) }}
                          transition={{ duration: 1.2, ease: "easeOut" }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="font-display text-2xl font-black" style={{ color: v.color }}>{auditData.overall_score}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-0.5">ASO Health Score</p>
                      <p className="font-display text-2xl font-extrabold" style={{ color: v.color }}>{v.label}</p>
                      <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                        {auditData.overall_score < 60
                          ? "Significant install lift available — fix the gaps below."
                          : "Solid baseline — fine-tuning could unlock 20–40% more installs."}
                      </p>
                    </div>
                  </div>

                  {/* 4 sub-scores — same categories as the full report */}
                  <div className="space-y-2 mb-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2">Score Breakdown</p>
                    {Object.entries(auditData.scores)
                      .sort(([, a], [, b]) => a - b)   // weakest first
                      .map(([key, score]) => {
                        const meta = SCORE_META[key] || { label: key, icon: BarChart2 };
                        const Icon = meta.icon;
                        const vv = verdict(score);
                        return (
                          <div key={key} className="flex items-center gap-3">
                            <div className="w-7 h-7 grid place-items-center bg-zinc-100 flex-shrink-0">
                              <Icon className="w-3.5 h-3.5 text-zinc-500" />
                            </div>
                            <span className="text-xs font-bold text-charcoal w-36 flex-shrink-0">{meta.label}</span>
                            <div className="flex-1 h-2 bg-zinc-100 overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }} animate={{ width: `${score}%` }}
                                transition={{ duration: 0.7, delay: 0.1 }}
                                className="h-full" style={{ background: vv.color }}
                              />
                            </div>
                            <span className="text-xs font-black w-7 text-right tabular-nums" style={{ color: vv.color }}>{score}</span>
                          </div>
                        );
                      })}
                  </div>

                  {/* Email gate */}
                  <div className="bg-charcoal text-white p-5 -mx-1">
                    <div className="flex items-start gap-2 mb-3">
                      <Lock className="w-4 h-4 text-electric flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-display text-base font-extrabold leading-tight">Want the full 12-point audit?</p>
                        <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                          Keywords, screenshot gaps, ratings velocity, conversion leaks, competitor comparison — emailed to you in under 2 minutes.
                        </p>
                      </div>
                    </div>
                    <form onSubmit={submitEmail}>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                        <input
                          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@yourapp.com"
                          className="w-full bg-white text-charcoal px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-electric pl-9"
                          autoFocus data-testid="exit-intent-email"
                        />
                      </div>
                      <button type="submit" disabled={emailLoading}
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-electric text-white px-5 py-3 font-bold text-sm hover:bg-blue-500 transition-colors disabled:opacity-60"
                        data-testid="exit-intent-email-submit"
                      >
                        {emailLoading
                          ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                          : <>Email me the full audit <ArrowRight className="w-4 h-4" /></>
                        }
                      </button>
                    </form>
                  </div>

                  {/* Or view report directly */}
                  <button
                    type="button"
                    onClick={() => { close(); navigate("/audit-report", { state: { preview: auditData } }); }}
                    className="mt-3 w-full text-center text-xs text-electric font-bold hover:underline"
                  >
                    View full report now (no email needed) →
                  </button>

                  <button
                    type="button"
                    onClick={() => { setAuditData(null); setScoreError(""); setPasteUrl(""); setSearchQuery(""); }}
                    className="mt-2 block text-xs text-zinc-400 hover:text-charcoal underline mx-auto"
                  >
                    ← Score a different app
                  </button>
                </>
              )}

              {/* ── Step 3: Confirmation ─────────────────────────── */}
              {done && (
                <div className="text-center py-6">
                  <div className="w-14 h-14 grid place-items-center bg-emerald-500 mx-auto mb-4 shadow-[3px_3px_0_0_#2D2D2D]">
                    <CheckCircle2 className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display text-2xl font-extrabold text-charcoal">Full audit sent!</h3>
                  <p className="mt-2 text-sm text-zinc-600 leading-relaxed max-w-sm mx-auto">
                    Your <strong className="text-charcoal">{auditData?.overall_score}/100 score</strong> with all sub-scores and fix recommendations landed at{" "}
                    <strong className="text-charcoal">{email}</strong>.
                  </p>
                  <div className="mt-6 flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => { close(); navigate("/audit-report", { state: { preview: auditData } }); }}
                      className="inline-flex items-center justify-center gap-2 bg-electric text-white px-5 py-3 font-bold text-sm hover:bg-blue-500 transition-colors w-full"
                    >
                      View Full Report <ArrowRight className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={close}
                      className="inline-flex items-center justify-center gap-2 bg-charcoal text-white px-5 py-3 font-bold text-sm hover:bg-zinc-800 transition-colors w-full">
                      Close
                    </button>
                  </div>
                </div>
              )}

            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
